/* eslint-disable arrow-body-style */

import React from 'react';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import { goToStudioDetailsPage } from '@gp/utils/studio';
import { FIELDS, FIELDS1, FIELDS2, FIELD_NAMES, getMetricInfoListUI } from '@gp/utils/metricTips';
import { RES_FIELDS_MAP, SORT_REQ_MAP } from '../constants';

import type { IRecord } from '@gp/types/global';
import type { StudioPerformanceDataType, IStudioPerformanceParams } from '../types/services';

const getMetricInfoListUICommon = (data?: any) => getMetricInfoListUI(FIELDS, FIELD_NAMES, data);

export const handleReqParams = (params: IStudioPerformanceParams): IStudioPerformanceParams => {
  const {
    // select_id_sub这个字段结构不要删除，这里是为了在restParams中不保留select_id_sub字段
    select_id_sub: selectIdSub,
    order_metric: orderMetric,
    ...restParams
  } = params || {};

  return { ...restParams, order_metric: SORT_REQ_MAP[orderMetric] };
};

const getActualTdOpt = (
  valueKey: string,
  yoyKey: string,
  momKey: string,
  isSelectYoy: boolean,
  isSelectMom: boolean,
) => {
  let tendencyOpt: any = null;
  if (isSelectYoy && isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
      isRenderDoubleTendency: true,
      doubleTendencyValue: momKey,
      doubleLabel: 'MoM',
    };
  } else if (isSelectYoy) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
    };
  } else if (isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: momKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'MoM',
    };
  }

  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 70 },
        },
        tendencyOpt,
      ],
    },
  };
};

const getBudgetCompletionTdOpt = (valueKey: string, percentKey: string) => {
  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 120 },
        },
        {
          type: 'percent',
          value: percentKey,
          isFormat: true,
          style: { marginLeft: 10, color: '#888' },
        },
      ],
    },
  };
};

export const getTableRecords = (loading: boolean, data: StudioPerformanceDataType) => {
  if (loading) return [];
  const list = data?.list;
  const records: IRecord<string | number | undefined | null>[] = [];
  if (!Array.isArray(list) || !list.length) return records;

  let allStudioItem = null;
  for (let i = 0, l = list?.length; i < l; i++) {
    const t = list?.[i];
    const item: IRecord<string | number | undefined | null> = {
      uniqueId: `${t?.studio_id}_${i}${1}`,
      cover: t?.cover,
      game_name: t?.game_name,
      studio_id: t?.studio_id,
      studio_name: t?.studio_name,
      bo: t?.bo,
      genre: t?.genre,
      sub_genre: t?.sub_genre,
      platform: Array.isArray(t?.platform) ? t?.platform?.join(',') : t?.platform,
      gross_revenue_monthly_actual: null,
      gross_revenue_monthly_actual_yoy: null,
      gross_revenue_monthly_actual_mom: null,
      gross_revenue_monthly_budget: null,
      gross_revenue_monthly_completion: null,
      gross_revenue_ytm_actual: null,
      gross_revenue_ytm_actual_yoy: null,
      gross_revenue_ytm_actual_mom: null,
      gross_revenue_annual_budget: null,
      gross_revenue_annual_completion: null,
      net_profit_monthly_actual: null,
      net_profit_monthly_actual_yoy: null,
      net_profit_monthly_actual_mom: null,
      net_profit_monthly_budget: null,
      net_profit_monthly_completion: null,
      net_profit_ytm_actual: null,
      net_profit_ytm_actual_yoy: null,
      net_profit_ytm_actual_mom: null,
      net_profit_annual_budget: null,
      net_profit_annual_completion: null,
    };
    const details = t?.details;
    if (Array.isArray(details)) {
      for (let j = 0, l = details?.length; j < l; j++) {
        const k = details?.[j];
        const key = RES_FIELDS_MAP?.[k?.metric]?.key;
        if (key) {
          item[key] = k?.value;
          if (RES_FIELDS_MAP?.[k?.metric]?.yoy) {
            item[`${key}_yoy`] = k?.yoy;
          }
          if (RES_FIELDS_MAP?.[k?.metric]?.mom) {
            item[`${key}_mom`] = k?.mom;
          }
        }
      }
    }
    if (item?.studio_name === 'All Studios') {
      allStudioItem = item;
      continue;
    }
    records.push(item);
  }

  if (allStudioItem) {
    records.unshift(allStudioItem);
  }

  return records;
};

export const getTableOptions = (
  module: 'studio' | 'project',
  loading: boolean,
  data: StudioPerformanceDataType,
  yoy: boolean,
  mom: boolean,
  defaultSort?: {
    by: string;
    order: 'asc' | 'desc';
  },
): any => {
  const grossRevenueDateTime = loading ? '-' : data?.date_time || '-';
  const netProfitDateTime = loading ? '-' : data?.date_time_net || '-';
  const studioColOpts = [
    {
      keyName: 'studio',
      title: 'Studio',
      isHasDoubleTitle: true,
      width: 240,
      align: 'left',
      fixed: 'left',
      tdOpt: {
        project: {
          domain: 'studio',
          src: 'cover',
          title: 'studio_name',
          platform: 'platform',
          toDetails: (cvm: Record<string, any>, e: React.SyntheticEvent<HTMLElement>, isJump: boolean) => {
            e?.stopPropagation();
            if (!isJump) return;
            goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
          },
        },
      },
    },
  ];
  const projectColOpts = [
    {
      keyName: 'project',
      title: 'Project',
      isHasDoubleTitle: true,
      width: 240,
      align: 'left',
      fixed: 'left',
      tdOpt: {
        project: {
          domain: 'game',
          src: 'cover',
          title: 'game_name',
          platform: 'platform',
          platformPlaceHolder: '-',
        },
      },
    },
    {
      keyName: 'studio',
      title: 'Studio',
      isHasDoubleTitle: true,
      width: 180,
      align: 'left',
      // fixed: 'left',
      tdOpt: {
        customize: {
          render: (cvm: any) => {
            return (
              <EllipsisTextBubble
                textElClassName="dbg-hover-active"
                text={cvm?.studio_name || '-'}
                onTextClick={(e) => {
                  e?.stopPropagation();
                  goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
                }}
              />
            );
          },
        },
      },
    },
  ];
  const initColOpt = module === 'studio' ? studioColOpts : projectColOpts;

  return {
    isHasAllLine: module === 'studio',
    columsOptions: [
      ...initColOpt,
      {
        keyName: 'gross_revenue_monthly_actual',
        title: 'Monthly Actual',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Cash Received From Players',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            dataTime: grossRevenueDateTime || '',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(
          'gross_revenue_monthly_actual',
          'gross_revenue_monthly_actual_yoy',
          'gross_revenue_monthly_actual_mom',
          yoy,
          mom,
        ),
      },
      {
        keyName: 'gross_revenue_monthly_budget_and_completion',
        title: 'Monthly Budget\n& Completion',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Aggregated Annual Budget',
            updateCadence: 'Yearly',
            dataTime: grossRevenueDateTime || '',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        width: 156,
        align: 'right',
        tdOpt: getBudgetCompletionTdOpt('gross_revenue_monthly_budget', 'gross_revenue_monthly_completion'),
      },
      {
        keyName: 'gross_revenue_ytm_actual',
        title: 'YTM Actual',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Cash Received From Players',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            dataTime: grossRevenueDateTime || '',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 210,
        align: 'right',
        tdOpt: getActualTdOpt(
          'gross_revenue_ytm_actual',
          'gross_revenue_ytm_actual_yoy',
          'gross_revenue_ytm_actual_mom',
          yoy,
          false,
        ),
      },
      {
        keyName: 'gross_revenue_annual_budget_and_completion',
        title: 'Annual Budget\n& Completion',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Aggregated Annual Budget',
            updateCadence: 'Yearly',
            dataTime: grossRevenueDateTime || '',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        width: 156,
        align: 'right',
        tdOpt: getBudgetCompletionTdOpt('gross_revenue_annual_budget', 'gross_revenue_annual_completion'),
      },
      {
        keyName: 'net_profit_monthly_actual',
        title: 'Monthly Actual',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Excluding All Below Line Items, Such As Interest Expenses And Taxes',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            dataTime: netProfitDateTime || '',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 210,
        align: 'right',
        tdOpt: getActualTdOpt(
          'net_profit_monthly_actual',
          'net_profit_monthly_actual_yoy',
          'net_profit_monthly_actual_mom',
          yoy,
          mom,
        ),
      },
      {
        keyName: 'net_profit_monthly_budget_and_completion',
        title: 'Monthly Budget\n& Completion',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Aggregated Annual Budget',
            updateCadence: 'Yearly',
            dataTime: netProfitDateTime || '',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        width: 156,
        align: 'right',
        tdOpt: getBudgetCompletionTdOpt('net_profit_monthly_budget', 'net_profit_monthly_completion'),
      },
      {
        keyName: 'net_profit_ytm_actual',
        title: 'YTM Actual',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Excluding All Below Line Items, Such As Interest Expenses And Taxes',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            dataTime: netProfitDateTime || '',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(
          'net_profit_ytm_actual',
          'net_profit_ytm_actual_yoy',
          'net_profit_ytm_actual_mom',
          yoy,
          false,
        ),
      },
      {
        keyName: 'net_profit_annual_budget_and_completion',
        title: 'Annual Budget\n& Completion',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Aggregated Annual Budget',
            updateCadence: 'Yearly',
            dataTime: netProfitDateTime || '',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        width: 156,
        align: 'right',
        tdOpt: getBudgetCompletionTdOpt('net_profit_annual_budget', 'net_profit_annual_completion'),
      },
    ],
    sortableOptions: {
      sortColumns: [
        'studio',
        'project',
        'gross_revenue_monthly_actual',
        'gross_revenue_monthly_budget_and_completion',
        'gross_revenue_ytm_actual',
        'gross_revenue_annual_budget_and_completion',
        'net_profit_monthly_actual',
        'net_profit_monthly_budget_and_completion',
        'net_profit_ytm_actual',
        'net_profit_annual_budget_and_completion',
      ],
      defaultSort: {
        by: 'gross_revenue_monthly_actual',
        order: 'desc',
        ...defaultSort,
      },
    },
    pageableOptions: {
      pageSizeOptions: [10, 20, 50, 100],
    },
    headerGroups: [
      {
        key: 'gross_revenue',
        align: 'center',
        title:
          module === 'project' ? (
            <span>Gross Revenue($) (${grossRevenueDateTime || '-'})</span>
          ) : (
            <MultipleLinesTextBubble
              fields={FIELDS1}
              names={FIELD_NAMES}
              data={{
                definition: `
                  Actual total sales amount.
                  <br />
                  Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
                `,
                source: 'Studio Reporting Package',
                updateCadence: 'Monthly',
              }}
            >
              <span>Gross Revenue($) (${grossRevenueDateTime || '-'})</span>
            </MultipleLinesTextBubble>
          ),
        children: [
          'gross_revenue_monthly_actual',
          'gross_revenue_monthly_budget_and_completion',
          'gross_revenue_ytm_actual',
          'gross_revenue_annual_budget_and_completion',
        ],
      },
      {
        key: 'net_profit',
        align: 'center',
        title:
          module === 'project' ? (
            <span>Net Profit($) (${netProfitDateTime || '-'})</span>
          ) : (
            <MultipleLinesTextBubble
              fields={FIELDS1}
              names={FIELD_NAMES}
              data={{
                definition:
                  "A studio's total earnings after subtracting all expenses, excluding all below line items, such as interest expenses and taxes.",
                source: 'Studio Reporting Package',
                updateCadence: 'Monthly',
              }}
            >
              <span>Net Profit($) (${netProfitDateTime || '-'})</span>
            </MultipleLinesTextBubble>
          ),
        children: [
          'net_profit_monthly_actual',
          'net_profit_monthly_budget_and_completion',
          'net_profit_ytm_actual',
          'net_profit_annual_budget_and_completion',
        ],
      },
    ],
  };
};
